import { memo, useMemo, useState } from 'react';
import {
    Alert,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import { ElementTile } from '../components/ElementTile';
import { GiftDetailDialog } from '../components/GiftDetailDialog';

import type { GiftSchemas } from '../../hooks/useGiftApi';
import { useGiftApi } from '../../hooks/useGiftApi';

import {
    compareDates,
    formatDateRange,
    getCalendarDateParts,
} from '../utils/dates';

type Gift = GiftSchemas['FullGiftAdminItem'];

interface GiftYearGroup {
    year: number | null;
    gifts: Gift[];
}

const groupGiftsByGiftYear = (gifts: readonly Gift[]): GiftYearGroup[] => {
    const groups = new Map<number | null, Gift[]>();

    for (const gift of gifts) {
        const year = getCalendarDateParts(gift.startDate)?.year ?? null;
        const group = groups.get(year) ?? [];
        group.push(gift);
        groups.set(year, group);
    }

    return [...groups.entries()]
        .sort(([first], [second]) => {
            if (first === null) return 1;
            if (second === null) return -1;
            return second - first;
        })
        .map(([year, yearGifts]) => ({
            year,
            gifts: yearGifts.toSorted((first, second) => {
                if (!first.startDate || !second.startDate) {
                    return first.name.localeCompare(second.name);
                }
                return (
                    compareDates(first.startDate, second.startDate) ||
                    first.name.localeCompare(second.name)
                );
            }),
        }));
};

const GiftLibraryYear = memo(function GiftLibraryYear({
    group,
    onSelect,
}: {
    group: GiftYearGroup;
    onSelect: (gift: Gift) => void;
}) {
    return (
        <Stack spacing={1.5}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                {group.year ?? 'Undated'}
            </Typography>
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 2 }}
            >
                <Table
                    size="small"
                    aria-label={`Gifts ${group.year ?? 'without a date'}`}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 64 }}>Icon</TableCell>
                            <TableCell sx={{ width: '20%' }}>
                                First published
                            </TableCell>
                            <TableCell>Gift</TableCell>
                            <TableCell sx={{ width: '30%' }}>
                                Publications
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {group.gifts.map((gift) => (
                            <TableRow
                                key={gift.id}
                                hover
                                tabIndex={0}
                                onClick={() => onSelect(gift)}
                                onKeyDown={(event) => {
                                    if (
                                        event.key === 'Enter' ||
                                        event.key === ' '
                                    ) {
                                        event.preventDefault();
                                        onSelect(gift);
                                    }
                                }}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell>
                                    <ElementTile
                                        gameElementIds={[
                                            ...gift.contents,
                                            gift.wrapperGameElementId,
                                        ]}
                                        tooltip={gift.name}
                                        ariaLabel={`${gift.name} icon`}
                                    />
                                </TableCell>
                                <TableCell>
                                    {formatDateRange(
                                        gift.startDate,
                                        null,
                                        true,
                                    ) ?? '—'}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>
                                    {gift.name}
                                </TableCell>
                                <TableCell>
                                    {gift.publications.length === 0
                                        ? 'No publication'
                                        : gift.publications
                                              .map((publication) =>
                                                  formatDateRange(
                                                      publication.startDate,
                                                      publication.endDate,
                                                      true,
                                                  ),
                                              )
                                              .filter(Boolean)
                                              .join(', ')}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );
});

export const GiftLibraryPage = () => {
    const giftApi = useGiftApi();
    const giftsQuery = giftApi.useQuery('get', '/gift/api/v2/admin/gifts');
    const groups = useMemo(
        () => groupGiftsByGiftYear(giftsQuery.data?.gifts ?? []),
        [giftsQuery.data?.gifts],
    );
    const [selectedGift, setSelectedGift] = useState<Gift | null>(null);

    if (giftsQuery.isPending) {
        return (
            <Stack sx={{ alignItems: 'center', gap: 2, py: 6 }}>
                <CircularProgress />
                <Typography color="text.secondary">Loading gifts...</Typography>
            </Stack>
        );
    }

    if (giftsQuery.isError) {
        return (
            <Alert severity="error">
                Unable to load the gift library. Please try again.
            </Alert>
        );
    }

    return (
        <>
            {groups.length === 0 ? (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        color: 'text.secondary',
                        borderRadius: 2,
                    }}
                >
                    No gifts found.
                </Paper>
            ) : (
                <Stack spacing={3}>
                    {groups.map((group) => (
                        <GiftLibraryYear
                            key={group.year ?? 'undated'}
                            group={group}
                            onSelect={setSelectedGift}
                        />
                    ))}
                </Stack>
            )}
            <GiftDetailDialog
                open={selectedGift !== null}
                onClose={() => setSelectedGift(null)}
                gift={selectedGift}
                initialTab="scheduled"
            />
        </>
    );
};
